import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCustomerAuth } from "@/lib/customerAuth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { LOGO_URL } from "@/lib/api";

export default function CustomerRegister() {
  const { register } = useCustomerAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", password: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSubmitting(true);
    try {
      await register(form);
      toast.success("Welcome to Bedding Lounge 🌿");
      navigate("/account");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-5 py-10 sm:py-16" data-testid="customer-register-page">
      <div className="text-center mb-8 sm:mb-10">
        <img src={LOGO_URL} alt="" className="h-14 w-14 mx-auto mb-4 object-contain" />
        <div className="label-eyebrow !text-[10px] sm:!text-xs mb-2">Join the Lounge</div>
        <h1 className="font-display text-[1.9rem] sm:text-4xl text-ink">Create account</h1>
        <p className="text-ink-soft text-sm mt-2">Save your details, check out faster, and track every order.</p>
      </div>
      <form onSubmit={submit} className="space-y-5">
        <div>
          <Label className="text-ink-soft text-xs uppercase tracking-widest">Full Name</Label>
          <Input required autoComplete="name" value={form.full_name} onChange={update("full_name")} className="mt-1.5 h-12 bg-cream border-border-subtle" data-testid="register-full-name" />
        </div>
        <div>
          <Label className="text-ink-soft text-xs uppercase tracking-widest">Email</Label>
          <Input type="email" inputMode="email" autoComplete="email" required value={form.email} onChange={update("email")} className="mt-1.5 h-12 bg-cream border-border-subtle" data-testid="register-email" />
        </div>
        <div>
          <Label className="text-ink-soft text-xs uppercase tracking-widest">Phone</Label>
          <Input type="tel" inputMode="tel" autoComplete="tel" required value={form.phone} onChange={update("phone")} placeholder="03XX-XXXXXXX" className="mt-1.5 h-12 bg-cream border-border-subtle" data-testid="register-phone" />
        </div>
        <div>
          <Label className="text-ink-soft text-xs uppercase tracking-widest">Password</Label>
          <Input type="password" autoComplete="new-password" required minLength={6} value={form.password} onChange={update("password")} className="mt-1.5 h-12 bg-cream border-border-subtle" data-testid="register-password" />
          <p className="text-[11px] text-ink-soft mt-1">Minimum 6 characters.</p>
        </div>
        <Button type="submit" disabled={submitting} className="w-full bg-sage hover:bg-sage-dark text-cream py-6 text-xs uppercase tracking-[0.22em] font-semibold transition-colors" data-testid="register-submit">
          {submitting ? "Creating…" : "Create Account"}
        </Button>
      </form>
      <div className="mt-6 text-center text-sm text-ink-soft">
        Already have an account?{" "}
        <Link to="/login" className="text-sage hover:text-sage-dark font-semibold" data-testid="link-to-login">
          Sign in
        </Link>
      </div>
    </div>
  );
}
